import React, { useState, useEffect } from 'react';
import { throttle } from 'lodash';

// Lazy load all section components
const IntroSection = React.lazy(() => import('./0intro'));
const AboutSpacesSection = React.lazy(() => import('./1AboutSpaces'));
const TestimonialSection = React.lazy(() => import('./1Testimonial'));
const World = React.lazy(() => import('./1World'));
const ProductSection = React.lazy(() => import('./1Product'));
const TestimonialNumbers = React.lazy(() => import('./1Statsbyindustry'));
const MilestonesSection = React.lazy(() => import('./2Milestones'));
const RoadmapSection = React.lazy(() => import('./2RoadmapDetail'));
const FounderSection = React.lazy(() => import('./4Founder'));
const ConclusionSection = React.lazy(() => import('./5Closer'));

// Navigation configuration
const NAVIGATION_CONFIG = [
  {
    id: 'product',
    label: 'SOOT',
    sections: ['about-spaces-section', 'product-section'],
    startAt: 'intro-section',
    endAt: 'testimonial-section'
  },
  {
    id: 'world',
    label: 'WORLD',
    sections: ['world-section'],
    endAt: 'testimonial-numbers-section'
  },
  {
    id: 'users',
    label: 'USERS',
    sections: ['testimonial-section', 'testimonial-numbers-section'],
    endAt: 'roadmap-section'
  },
  {
    id: 'roadmap',
    label: 'ROADMAP',
    sections: ['roadmap-section', 'roadmap-detail-section'],
    endAt: 'founder-section'
  },
  {
    id: 'founder',
    label: 'FOUNDER',
    sections: ['founder-section'],
    endAt: 'closer-section'
  }
];

// Navigation Progress Component
const NavigationProgress = () => {
  const [activeSection, setActiveSection] = useState('');
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [isIntroVisible, setIsIntroVisible] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isCloserVisible, setIsCloserVisible] = useState(false);

  // Handle scroll to section
  const scrollToSection = (sectionId) => {
    const targetId = NAVIGATION_CONFIG.find(config => config.id === sectionId)?.sections[0];
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Setup intersection observers
  useEffect(() => {
    const observers = [];

    // Intro section observer
    const introObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsIntroVisible(entry.intersectionRatio > 0.1);
        });
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5],
        rootMargin: '-20% 0px -80% 0px'
      }
    );

    // Closer section observer
    const closerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsCloserVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    // Section observers
    NAVIGATION_CONFIG.forEach(config => {
      config.sections.forEach(sectionId => {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              setVisibleSections(prev => {
                const newSet = new Set(prev);
                if (entry.isIntersecting) {
                  newSet.add(config.id);
                } else {
                  // Only remove if no other section from this config is visible
                  const otherSectionsVisible = config.sections
                    .filter(id => id !== sectionId)
                    .some(id => {
                      const el = document.getElementById(id);
                      const rect = el?.getBoundingClientRect();
                      return rect && rect.top < window.innerHeight && rect.bottom > 0;
                    });
                  if (!otherSectionsVisible) {
                    newSet.delete(config.id);
                  }
                }
                return newSet;
              });
            });
          },
          {
            threshold: [0, 0.3, 0.7, 1],
            rootMargin: '-20% 0px -80% 0px'
          }
        );

        const element = document.getElementById(sectionId);
        if (element) {
          observer.observe(element);
          observers.push(observer);
        }
      });

      // Boundary observers
      if (config.endAt) {
        const boundaryObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                setVisibleSections(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(config.id);
                  return newSet;
                });
              }
            });
          },
          {
            threshold: [0, 0.3, 0.7, 1],
            rootMargin: '-40% 0px -60% 0px'
          }
        );

        const element = document.getElementById(config.endAt);
        if (element) {
          boundaryObserver.observe(element);
          observers.push(boundaryObserver);
        }
      }
    });

    // Initialize observers
    const introElement = document.getElementById('intro-section');
    const closerElement = document.getElementById('closer-section');

    if (introElement) introObserver.observe(introElement);
    if (closerElement) closerObserver.observe(closerElement);

    // Handle initial scroll position
    const handleScroll = throttle(() => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const isAtTop = scrollTop < window.innerHeight * 0.5;
      setIsIntroVisible(isAtTop);
      if (isInitialLoad && !isAtTop) {
        setIsInitialLoad(false);
      }
    }, 100);

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    // Update active section based on visible sections
    const updateActiveSection = throttle(() => {
      if (visibleSections.size === 0) {
        setActiveSection('');
        return;
      }
      
      for (let i = NAVIGATION_CONFIG.length - 1; i >= 0; i--) {
        if (visibleSections.has(NAVIGATION_CONFIG[i].id)) {
          setActiveSection(NAVIGATION_CONFIG[i].id);
          break;
        }
      }
    }, 100);

    window.addEventListener('scroll', updateActiveSection);
    updateActiveSection();

    // Cleanup
    return () => {
      observers.forEach(observer => observer.disconnect());
      introObserver.disconnect();
      closerObserver.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', updateActiveSection);
    };
  }, [visibleSections, isInitialLoad]);

  // Render navigation progress
  return (
    <div 
      className={`fixed bottom-0 left-0 w-full p-4 bg-gradient-to-t from-black/20 to-transparent z-50 transition-all duration-500 transform ${
        isCloserVisible || isIntroVisible || isInitialLoad
          ? 'opacity-0 translate-y-full pointer-events-none invisible' 
          : 'opacity-100 translate-y-0 visible'
      }`}
    >
      <div className="max-w-4xl mx-auto flex items-center">
        <div className="flex items-center gap-8">
          {NAVIGATION_CONFIG.map(section => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className={`text-white font-bold text-lg tracking-wide transition-all duration-500 hover:opacity-100 cursor-pointer ${
                activeSection === section.id
                  ? 'opacity-100 scale-110'
                  : 'opacity-50 scale-100 hover:scale-105'
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <div className="relative w-full min-h-screen">
      {/* Background Elements */}
      <div className="fixed inset-0 bg-[#e9e9e9] -z-10" />
      <div className="fixed inset-0 bg-gradient-to-b from-transparent via-transparent to-[#2E2E2E] opacity-0 transition-opacity duration-1000 -z-10 dark-gradient" />
      
      {/* Main Content */}
      <div className="relative">
        <React.Suspense fallback={
          <div className="w-full h-screen flex items-center justify-center">
            <div className="animate-pulse text-2xl text-gray-600">Loading...</div>
          </div>
        }>
          {/* Sections */}
          <section id="intro-section" className="w-full">
            <IntroSection />
          </section>
          
          <section id="about-spaces-section" className="w-full">
            <AboutSpacesSection />
          </section>
          
          <section id="product-section" className="w-full">
            <ProductSection />
          </section>
          
          <section id="testimonial-section" className="w-full">
            <TestimonialSection />
          </section>

          <section id="world-section" className="w-full">
            <World />
          </section>

          <section id="testimonial-numbers-section" className="w-full">
            <TestimonialNumbers />
          </section>
          
          <section id="roadmap-section" className="w-full">
            <MilestonesSection />
          </section>
          
          <section id="roadmap-detail-section" className="w-full">
            <RoadmapSection />
          </section>

          <section id="founder-section" className="w-full">
            <FounderSection />
          </section>
          
          <section id="closer-section" className="w-full transition-colors duration-1000">
            <ConclusionSection />
          </section>

          {/* Navigation Progress */}
          <NavigationProgress />
        </React.Suspense>
      </div>

      {/* Global Styles */}
      <style jsx global>{`
        .dark-gradient {
          opacity: 0;
        }
        
        .dark-gradient.active {
          opacity: 1;
        }
        
        html {
          scroll-behavior: smooth;
        }
        
        @media (prefers-reduced-motion: reduce) {
          html {
            scroll-behavior: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default App;